import { Badge, Button, Callout, Card, CardHeader, Tooltip } from '@provenance/design';
import type { CheckPanel as CheckPanelData } from '@provenance/shared';
import {
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Loader2,
  RefreshCw,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { relative } from '../../lib/format';

/**
 * The Completeness & Consistency panel (spec §8).
 *
 * Design decisions that matter here, because this panel sits next to a button that
 * applies a chamber's legal authority:
 *
 *  IT IS ADVISORY, AND IT SAYS SO. Nothing here disables Approve. The operator is the
 *  decision-maker (§8.4). A panel that looked like a gate would train operators to treat
 *  it as one, and then to resent it.
 *
 *  IT DISTINGUISHES THE TWO ENGINES. Every finding is labelled Rule or AI. A
 *  deterministic arithmetic check and a language model's opinion deserve different
 *  amounts of trust, and hiding which is which would be dishonest.
 *
 *  UNAVAILABLE IS A STATED OUTCOME. When the semantic layer cannot run, the panel says
 *  so in plain words. An empty panel is indistinguishable from a clean document, and
 *  those are very different things.
 *
 *  FAILURES FIRST. An operator scanning this reads top to bottom and stops early.
 */
export function CheckPanel({
  checks,
  onRecheck,
  rechecking,
  certificateOnly = false,
}: {
  checks: CheckPanelData;
  onRecheck: () => void;
  rechecking: boolean;
  /** Emailed or uploaded: nothing was submitted to compare the certificate with. */
  certificateOnly?: boolean;
}) {
  const running = checks.state === 'QUEUED' || checks.state === 'RUNNING';
  // The "checked against the certificate itself" note used to be a warning on every emailed
  // or uploaded request. It describes the check, not the certificate, so it is said once in
  // the panel's description instead. Rows stored before that change are hidden here.
  const basis = checks.results.filter((r) => r.checkType === 'CHECK_BASIS');
  const warnings = checks.summary.warning - basis.filter((r) => r.status === 'WARNING').length;
  const ordered = checks.results.filter((r) => r.checkType !== 'CHECK_BASIS').sort(
    (a, b) => severity(b.status) - severity(a.status) || a.title.localeCompare(b.title),
  );

  return (
    <Card>
      <CardHeader
        title="Completeness & consistency"
        hint={checks.ranAt ? relative(checks.ranAt) : undefined}
        description={
          certificateOnly
            ? 'Read from the certificate, with nothing submitted to compare it to. Advisory only: it never blocks a decision.'
            : 'Advisory only. It never blocks a decision — you can approve or reject whatever it says.'
        }
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={onRecheck}
            loading={rechecking || running}
            leadingIcon={<RefreshCw className="size-3.5" />}
          >
            Re-check
          </Button>
        }
      />

      {running ? (
        <div className="flex items-center gap-2.5 py-6 [font-size:13px] text-[var(--pv-muted)]">
          <Loader2 aria-hidden className="size-4 animate-spin" />
          Reconciling the document against the submitted data…
        </div>
      ) : (
        <>
          <div className="mb-3.5 flex flex-wrap items-center gap-2">
            {checks.summary.fail > 0 && <Badge tone="stop" dot>{checks.summary.fail} to look at</Badge>}
            {warnings > 0 && (
              <Badge tone="warn" dot>
                {warnings} warning{warnings === 1 ? '' : 's'}
              </Badge>
            )}
            {checks.summary.pass > 0 && <Badge tone="ok">{checks.summary.pass} passed</Badge>}
            <div className="flex-1" />
            {/* Which engines actually ran. Never implied — always stated. */}
            <Tooltip
              content={
                checks.semanticLayerRan
                  ? 'Deterministic rules plus an AI reconciliation of the document against the submitted data.'
                  : 'Deterministic rules only. The AI reconciliation layer did not run for this document.'
              }
            >
              <span className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                {checks.semanticLayerRan ? (
                  <>
                    <Sparkles aria-hidden className="size-3" /> Rules + AI
                  </>
                ) : (
                  <>
                    <HelpCircle aria-hidden className="size-3" /> Rules only
                  </>
                )}
              </span>
            </Tooltip>
          </div>

          {checks.unavailableReason && (
            <Callout
              tone="info"
              icon={<HelpCircle className="size-4" />}
              className="mb-3.5"
              title="The AI layer did not run"
            >
              {checks.unavailableReason}
            </Callout>
          )}

          {ordered.length === 0 ? (
            <p className="py-4 [font-size:13px] text-[var(--pv-muted)]">
              No checks have been recorded for this document yet.
            </p>
          ) : (
            <ul className="flex list-none flex-col gap-0 p-0">
              {ordered.map((result) => (
                <li
                  key={result.id}
                  className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 border-b border-[var(--pv-line)] py-3 last:border-b-0 last:pb-0"
                >
                  <span className="mt-0.5">{icon(result.status)}</span>
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-baseline gap-2">
                      <span className="[font-size:13.5px] font-semibold">{result.title}</span>
                      <span className="[font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">
                        {result.source === 'LLM' ? 'AI' : 'Rule'}
                      </span>
                    </p>
                    <p className="mt-1 [font-size:13px] leading-relaxed text-[var(--pv-ink-soft)]">
                      {result.message}
                    </p>
                    {(result.expected || result.found) && (
                      <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 [font-size:var(--pv-text-xs)]">
                        {result.found && (
                          <div className="flex gap-1.5">
                            <dt className="text-[var(--pv-faint)]">On the document</dt>
                            <dd className="pv-mono m-0 font-medium">{result.found}</dd>
                          </div>
                        )}
                        {result.expected && (
                          <div className="flex gap-1.5">
                            <dt className="text-[var(--pv-faint)]">{certificateOnly ? 'Expected' : 'Submitted'}</dt>
                            <dd className="pv-mono m-0 font-medium">{result.expected}</dd>
                          </div>
                        )}
                      </dl>
                    )}
                    {result.fieldRef && (
                      <p className="pv-mono mt-1.5 [font-size:11px] text-[var(--pv-faint)]">{result.fieldRef}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

function severity(status: string): number {
  return ({ FAIL: 3, WARNING: 2, UNAVAILABLE: 1, PASS: 0 } as Record<string, number>)[status] ?? 0;
}

function icon(status: string) {
  switch (status) {
    case 'FAIL':
      return <XCircle aria-hidden className="size-4 text-[var(--pv-stop)]" />;
    case 'WARNING':
      return <AlertTriangle aria-hidden className="size-4 text-[var(--pv-warn)]" />;
    case 'UNAVAILABLE':
      return <HelpCircle aria-hidden className="size-4 text-[var(--pv-faint)]" />;
    default:
      return <CheckCircle2 aria-hidden className="size-4 text-[var(--pv-ok)]" />;
  }
}
